/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1764028800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Nov 25 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sqlserver",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Azure firewall setup",
          "depth": 0,
          "id": "azure-firewall-setup"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Choose authentication method",
          "depth": 1,
          "id": "choose-authentication-method"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      em: "em",
      ol: "ol",
      li: "li",
      a: "a",
      strong: "strong",
      img: "img",
      h3: "h3",
      h4: "h4",
      code: "code",
      ul: "ul",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet} = _components;
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in Microsoft SQL Server and push it to downstream destinations. Connecting Hightouch to Microsoft SQL Server may require some setup in both platforms. If using an Azure provisioned Microsoft SQL Server, you need to configure your firewall settings."
      }), "\n", _jsx(WhitelistDetails, {
        target: "SQL Server instance",
        privatelink: {
          supportedIntegrations: ["aws-privatelink", "gcp-psc"]
        }
      }), "\n", _jsx(_components.h2, {
        id: "azure-firewall-setup",
        children: "Azure firewall setup"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "This section only applies when connecting to SQL Server instances hosted in Azure."
        })
      }), "\n", _jsx(_components.p, {
        children: "By default, Azure protects your SQL Server from any external IP address from connecting to your SQL Server. Follow these steps to add Hightouch's IP addresses to the firewall:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Log into your ", _jsx(_components.a, {
            href: "https://portal.azure.com/#home",
            children: "Azure Console Dashboard"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "SQL databases"
          }), " under ", _jsx(_components.strong, {
            children: "Services"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the SQL database you want to update."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Set server firewall"
          }), " .\n", _jsx(_components.img, {
            src: "sources/source-sqlserver-firewall.png",
            alt: "Azure Console Dashboard with settings"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Public network access"
          }), ", select ", _jsx(_components.strong, {
            children: "Selected networks"
          }), ".\n", _jsx(_components.img, {
            src: "sources/source-sqlserver-firewall-setup-1.png",
            alt: "Azure Console Dashboard with settings"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Scroll to ", _jsx(_components.strong, {
            children: "Firewall rules"
          }), " and click ", _jsx(_components.strong, {
            children: "Add a firewall rule"
          }), ".\n", _jsx(_components.img, {
            src: "sources/source-sqlserver-firewall-setup-2.png",
            alt: "Azure Console Dashboard with settings"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Allowlist the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/security/networking#ip-addresses",
            children: "Hightouch IP addresses"
          }), " for your region."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click the ", _jsx(_components.strong, {
            children: "Save"
          }), " button on the bottom left."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["In Hightouch, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Microsoft SQL Server"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-authentication-method",
        children: "Choose authentication method"
      }), "\n", _jsxs(_components.p, {
        children: ["When creating a SQL Server source, you can either enter the Host, Port, Database, User, and Password by selecting ", _jsx(_components.strong, {
          children: "Password"
        }), " or you can provide the full URI ", _jsx(_components.strong, {
          children: "Connection string"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "connection-string",
        children: "Connection string"
      }), "\n", _jsxs(_components.p, {
        children: ["Enter your connection string in this format: ", _jsx(_components.code, {
          children: "Server=server;Database=database;User Id=username;Password=password;Encrypt=boolean"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've entered your connection string and clicked ", _jsx(_components.strong, {
          children: "Continue"
        }), ", you can skip to the ", _jsx(_components.a, {
          href: "#test-your-connection",
          children: "Test your Connection"
        }), " section of this doc."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "SQL Server"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your Microsoft SQL Server. If using the hostname, make sure to only insert the hostname and not the full URL (remove the leading ", _jsx(_components.code, {
            children: "http://"
          }), " and the final ", _jsx(_components.code, {
            children: "/"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your Microsoft SQL Server. The default is 1433, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": The name of the database in your Microsoft SQL Server."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User"
          }), ": This can be your personal Microsoft SQL Server login or a dedicated user for Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Optionally you can enter a ", _jsx(_components.strong, {
          children: "Request timeout duration"
        }), " and whether to ", _jsx(_components.strong, {
          children: "Trust server certification"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up Microsoft SQL Server as a source for the first time, Hightouch validates your credentials and access to your database. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you see the following error message, you need to ", _jsx(_components.a, {
          href: "#azure-firewall-setup",
          children: "update your firewall settings"
        }), " in your Azure Console."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: ["Cannot open server 'hightouch-test' request by login.\nClient with IP address '", _jsx(_components.span, {
            className: "hljs-number",
            children: "54.196"
          }), _jsx(_components.span, {
            className: "hljs-number",
            children: ".30"
          }), _jsx(_components.span, {
            className: "hljs-number",
            children: ".169"
          }), "' is not allowed to access the server.\nTo enable access", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " use the Windows Azure Management Portal or run sp_set_firewall_rule\non the master database to create a firewall rule for this IP address or address range.\nIt may up take up to five minutes for this change to take effect.\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, you've completed setting up Microsoft SQL Server as a source. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Microsoft SQL Server."]
      }), "\n", _jsx(_components.p, {
        children: "The Microsoft SQL Server source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "dbt models"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.p, {
        children: "Most issues with the Microsoft SQL Server source can be resolved by verifying the firewall settings according to the setup instructions above."
      }), "\n", _jsx(_components.p, {
        children: "If you see an error message similar to the following"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-json",
          children: "Login failed for user '<username>'\n"
        })
      }), "\n", _jsx(_components.p, {
        children: "or"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-json",
          children: "login failed for user '<domain>\\<username>'\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["verify that the username and password work locally. You may also need to adjust your Microsoft SQL Service authentication mode. See ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/sql/relational-databases/errors-events/mssqlserver-18456-database-engine-error?view=sql-server-ver16#login-failed-for-user-username-or-login-failed-for-user-domainusername",
          children: "Microsoft's documentation"
        }), " for more information about login errors and authentication modes."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
